import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Button, IconButton } from '@material-ui/core';
import { ChevronLeft, ChevronRight, Close, ExpandLess, ExpandMore, Publish } from '@material-ui/icons';
import ScaleLoader from 'react-spinners/ScaleLoader';

import useLiveManagerActions from './useLiveManagerActions';
import { ReactComponent as ScreenRecordIcon } from '../../../assets/images/screen_record.svg';
import ConfirmationDialog from '../../../reusable/ConfirmationDialog/ConfirmationDialog';
import { confirmToSlides, convertToSlides } from '../../../actions/slidesActions';
import { showMessage } from '../../../actions/globalActions';
import { deleteMultipleResources, getMediaQueryDetails, saveInspectScreenDetails } from '../../../actions/publishActions';
import { liveEventStatusList } from '../../../constants/liveEventStatus';
import { streamSetupType } from '../../../constants/liveManagerConstants';
import { mediaNames, messageTypes } from '../../../constants/mediaConstants';
import { getSecondsFromTimelineTime, getTimelineStartDate } from '../../../services/timeStampService';
import { getDetails } from '../../../services/mediaDisplayService';

import styles from './LiveManagerPresenter.module.scss';
import { generateId } from '../../../services/stringHelperService';
import { setLoadingOverlayText, setRemovingPresentation } from '../../../actions/liveManagerActions';
import { setLiveQueueSlideItem } from '../../../actions/interactTimelineActions';
import { liveWebSocketEvents } from '../../../constants/liveChatConstant';
interface LiveManagerProps {
	reloadData?: () => void;
	mediaId: string;
	registerSocketMessageHandler?: (_handler: (_evt: MessageEvent) => void) => void;
	unregisterSocketMessageHandler?: (_handler: (_evt: MessageEvent) => void) => void;
};

const SLIDE_IDENTIFIER = "slide";

// How long a slide that is still open is given to be closed by whoever owns it before this
// closes it. Only the lifetime of an orphan depends on it, never the times written.
const ORPHAN_CLOSE_DELAY_MS = 1500;

const LiveManagerPresenter = React.memo(({
	mediaId,
	registerSocketMessageHandler,
	unregisterSocketMessageHandler,
}: LiveManagerProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch() as any;

	// used to avoid extra re-renders by getting the state from the store directly
	const store = useStore();

	const accountId = useSelector((state: any) => state.session.defaultAccountId);
	const { items: interactItems, liveQueueSlideItem } = useSelector((state: any) => state.interactTimelineReducer);
	const { loading: isFileLoading } = useSelector((state: any) => state.slidesReducer);
	const liveEventStatus = useSelector(
		(state: any) => state.liveManager.liveEventStatus,
		(a, b) => a === b // only re-render if liveEventStatus (string) actually changed
	);

	const fileInputRef = useRef<HTMLInputElement>(null);
	const stoppedPresentingRef = useRef<boolean>(false);
	const presentationStoppedFlagRef = useRef<boolean>(false);
	const flagSeededRef = useRef<boolean>(false);
	const isAutoAddRef = useRef<boolean>(false);
	const openSequencesRef = useRef<Map<string, { start: number; resourceId: string }>>(new Map());
	const closeOpenSequencesRef = useRef<((_endSeconds: number) => void) | null>(null);
	const liveQueueSlideItemRef = useRef<any>(null);
	const closedSequencesRef = useRef<Set<string>>(new Set());
	const orphanCloseTimerRef = useRef<any>(null);
	const [isJobOngoing, setIsJobOngoing] = useState<boolean>(false);
	const [newSlidePayload, setNewSlidePayload] = useState<any | null>(null);
	const [isSlideLoading, setIsSlideLoading] = useState<boolean>(false);
	const [loadingMsg, setLoadingMsg] = useState<string>('');
	const [showUploadSection, setShowUploadSection] = useState<boolean>(true);
	const [showPresentSection, setShowPresentSection] = useState<boolean>(false);
	const [mediaDetails, setMediaDetails] = useState<any>(null);
	const [fileName, setFileName] = useState<string>('');
	const [slides, setSlides] = useState<any[]>([]);
	const [slidesCount, setSlidesCount] = useState<number>(0);
	const [selectedSlide, setSelectedSlide] = useState<any | null>(null);
	const [tempSlide, setTempSlide] = useState<any>(null);
	const [hasPresentationStarted, setHasPresentationStarted] = useState<boolean>(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
	const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
	const [showRemoveFileWarningDialog, setShowRemoveFileWarningDialog] = useState<"no-delete" | "delete" | "">("");

	const { addInteractItemToTracks, addToLiveQueue, addToLiveQueueAtStart, removeSlideTrack, findAndSetExistingTrack, closeSequenceAt } = useLiveManagerActions({ mediaId, setMediaDetails, closeOpenSequencesRef });

	const getSlideImgSrc = (slide: any):string => {
		if (!slide || !slide.renditions?.length) {
			return '';
		}
		return slide.renditions[0]?.links[0]?.href;
	};

	const getSlideImgName = (slide: any):string => {
		return getSlideImgSrc(slide).split("/").pop()!;
	};

	const getSlideIndex = (slide: any):number => {
		return parseInt(slide.id.split("-").pop()!, 10);
	};

	const imgUrl = slides.length && getSlideImgSrc(slides[0]);
	const imgName = slides.length && getSlideImgName(slides[0]);

	// Once the event has gone live the slide timeline is published: viewers' players and the
	// speakers' presenter panels have already read it, so the deck is frozen from here on. Before
	// that the deck is only a draft and a wrong upload can still be replaced.
	//
	// Read from the store rather than closed over at render time: the callers include the debounced
	// callbacks below, which are created once on the first render and would otherwise see the
	// event as never-started forever. `custom.livemanager.start` is set when the event starts and
	// never cleared — callers holding a freshly fetched media pass it in, since the pop-out windows
	// mount this panel without the top panel that puts those times in the store.
	const hasEventGoneLive = (media?: any) => {
		const { liveEventStatus: status, liveEventStartTime } = store.getState().liveManager;
		return (
			status !== liveEventStatusList.INITIAL ||
			!!liveEventStartTime ||
			!!(media ?? mediaDetails)?.custom?.livemanager?.start
		);
	};

	// only blocked while something is already in flight — going live is handled by the dialog,
	// which explains why the file can no longer be removed instead of silently doing nothing
	const isRemoveFileDisabled = useMemo(
		() => !mediaDetails || isSlideLoading || isJobOngoing || isFileLoading,
		[mediaDetails, isSlideLoading, isJobOngoing, isFileLoading]
	);

	const uploadPdfFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.length) {
			const file = e.target.files[0];
			setFileName(file.name);
			setIsJobOngoing(true);
			setLoadingMsg(t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_LOADING_PDF'));
			dispatch(convertToSlides(accountId, {
				file,
				moreParams: {
					media_id: mediaId,
					asset_id: mediaDetails?.asset.id
				}
			})).then((resp: any) => {
				if (resp.data && resp.data.ok && resp.data.job_id) {
					debouncedJobConfirm(accountId, resp.data.job_id);
				} else {
					setIsJobOngoing(false);
					dispatch(showMessage('Something went wrong.', messageTypes.error));
				}
			});
		}
	};

	const imagesRemovedCallback = (media: any) => {
		const hasSlides = (media?.asset?.resources ?? []).some((i: any) => i.id.includes(SLIDE_IDENTIFIER));
		if (hasSlides) {
			// call the deounce function again until the images are fully removed
			debouncedFetchMedias(accountId, imagesRemovedCallback);
		} else {
			// mediaDetails has to be refreshed together with the slides: the effect below rebuilds
			// `slides` from it whenever `slides` is empty, so a stale copy would bring the deleted
			// deck straight back.
			setMediaDetails(media);
			setFileName('');
			setShowPresentSection(false);
			setShowUploadSection(true);
			setSlides([]);
			setSelectedSlide(null);
			setHasPresentationStarted(false);
			setIsSlideLoading(false);
			setLoadingMsg('');
			dispatch(setRemovingPresentation(false));
		}
	};

	const handleClickRemoveFile = () => {
		setShowRemoveFileWarningDialog(hasEventGoneLive() ? "no-delete" : "delete");
	}

	const removeFile = async () => {
		const assetId = mediaDetails?.asset.id;
		if (hasEventGoneLive() || !assetId || !slides.length) {
			return;
		}
		setIsSlideLoading(true);
		setLoadingMsg(t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_REMOVING_FILE'));
		// holds the go-live button until this is finished: starting the event midway through would
		// leave a running stream with a half-deleted presentation
		dispatch(setRemovingPresentation(true));
		// the first slide is auto-presented on upload, so there is always a sequence to clean up.
		// Block the socket handler from putting it back while the deck is being taken apart.
		stoppedPresentingRef.current = true;
		isAutoAddRef.current = false;
		setNewSlidePayload(null);

		// The timeline goes before the resources it points at: a sequence left on the pdf-track
		// would reference a slide that no longer exists, and a re-upload reuses the same resource
		// ids ('slide-1', 'slide-2', ...), so the stale sequence would silently latch onto the new
		// deck's first page.
		const trackRemoval = await removeSlideTrack();
		if (trackRemoval !== true) {
			// nothing was removed, so the deck is still being presented: let the socket handler
			// follow it again, otherwise the panel stops tracking slide changes for good
			stoppedPresentingRef.current = false;
			setIsSlideLoading(false);
			setLoadingMsg('');
			dispatch(setRemovingPresentation(false));
			dispatch(showMessage(
				trackRemoval === 'live'
					? t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_UPLOADED_FILE_WARNING_MSG')
					: t('COMMON_DELETION_FAILED'),
				messageTypes.error
			));
			return;
		}
		setSelectedSlide(null);
		setHasPresentationStarted(false);
		setSlidesCount(0);

		const assetIds = Array(slides.length).fill(assetId);
		const resouceIds = slides.map((i: any) => i.id);
		dispatch(deleteMultipleResources(accountId, assetIds, resouceIds)).then(() => {
			// call the deounce function again until the images are fully removed
			debouncedFetchMedias(accountId, imagesRemovedCallback);
		});
	};

	const imagesReadyCallback = (media: any) => {
		const slideResources = !media || !media.asset || media.asset.resources.filter((i: any) => i.id.includes(SLIDE_IDENTIFIER));
		const isFail = slideResources.some((i: any) => !i.renditions || !i.renditions.length);
		// check if the images are ready and if all of them are returned (because the backend may not return all right away)
		if (isFail || (slidesCount && slidesCount > slideResources.length)) {
			// call the deounce function again until the images are ready
			debouncedFetchMedias(accountId, imagesReadyCallback);
		} else {
			setMediaDetails(media);
			// create 1st slide if no slides exist yet
			const slideResources = media?.asset?.resources.filter((i: any) => i.id.includes(SLIDE_IDENTIFIER));
			if (slideResources?.[0]) {
				// A deck uploaded before the event goes live is presented from the very start of the
				// timeline, so viewers have slide 1 from the first frame. A deck uploaded while the
				// stream is already running starts where the stream is now — back-dating it to 0
				// would make the recording claim slide 1 was on screen before it was uploaded.
				isAutoAddRef.current = !hasEventGoneLive(media);
				setHasPresentationStarted(true);
				presentSlide(slideResources[0], 0);
			}
			setIsJobOngoing(false);
		}
	};

	// sends requests every second to check if the slides are created on the backend
	// because it may take for the backend several seconds to complete this operation
	const debouncedJobConfirm = useCallback(
		debounce((accountId: string, jobId: string) => {
			dispatch(confirmToSlides(accountId, jobId)).then((resp: any) => {
				if (resp.ok && resp.job) {
					if (resp.job.status !== "SUCCESS") {
						debouncedJobConfirm(accountId, jobId);
					} else {
						if (resp.job.steps?.length) {
							const stepWithOutput = resp.job.steps?.find((i: any) => i.result.OUTPUT);
							if (stepWithOutput) {
								const output = JSON.parse(stepWithOutput.result.OUTPUT);
								setSlidesCount(output?.pages?.length || 0);
							}
						}
						// sends repetitive requests to fetch the media until the images are ready
						debouncedFetchMedias(accountId, imagesReadyCallback);
					}
				}
			});
		}, 1000), []
	);

	const debouncedFetchMedias = useCallback(
		debounce((accountId: string, callback: (_media: any) => void) => {
			dispatch(getMediaQueryDetails(accountId, mediaId, mediaNames.medias)).then((media: any) => {
				callback(media);
			});
		}, 500), []
	);

	// uses a small delay to avoid fast clicking
	const debouncedPresent = useCallback(
		debounce((slide: any, index: number) => {
			presentSlide(slide, index);
		}, 500), []
	);

	// the stream is considered "not started" while it's stopped or its position is still at the timeline start
	const isStreamNotStarted = () => {
		const state = store.getState();
		const { liveEventStatus, liveStreamPositionTime } = state.liveManager;
		return (
			liveEventStatus === liveEventStatusList.STOPPED ||
			(liveStreamPositionTime.getTime && liveStreamPositionTime.getTime() == getTimelineStartDate().getTime())
		);
	};

	const presentPrevSlide = () => {
		if (!selectedSlide) {
			return;
		}
		if (isStreamNotStarted()) { // ask the admin to start the live first
			setShowWarningDialog(true);
			return;
		}
		const index = selectedSlide.index - 1;
		const newIndex = index < 0 ? slides.length - 1 : index;
		debouncedPresent(slides[newIndex], newIndex);
	};

	const presentNextSlide = () => {
		if (!selectedSlide) {
			return;
		}
		if (isStreamNotStarted()) { // ask the admin to start the live first
			setShowWarningDialog(true);
			return;
		}
		const index = selectedSlide.index + 1;
		const newIndex = index >= slides.length ? 0 : index;
		debouncedPresent(slides[newIndex], newIndex);
	};

	// Presenting is only implied by the track - a slide sequence with no end - so
	// stopping has no signal of its own: a close reads the same whether the admin
	// stopped or moved on. This flag is that missing signal, and the speaker view in
	// qbrick-meet listens for it. The media is re-read and merged before writing,
	// since the save carries the whole custom object.
	const setPresentationStoppedFlag = (stopped: boolean) => {
		// A stop is always written: it is the only thing the speaker view has to go on,
		// and skipping one leaves the speaker presenting after the admin stopped. Only
		// the clearing write is skipped when nothing changed, since presenting a slide
		// asks for it every time and every write notifies all clients.
		if (!stopped && presentationStoppedFlagRef.current === stopped) {
			return;
		}
		presentationStoppedFlagRef.current = stopped;

		dispatch(getMediaQueryDetails(accountId, mediaId, mediaNames.medias)).then((media: any) => {
			const details = getDetails(media, false);

			if (!details) {
				presentationStoppedFlagRef.current = !stopped;
				return;
			}

			const body = {
				id: details.id,
				asset: { id: details.asset?.id },
				catalog: {},
				metadata: details.metadata,
				tags: details.tags,
				custom: {
					...(details.custom ?? {}),
					livemanager: {
						...(details.custom?.livemanager ?? {}),
						// A timestamp rather than `true`, so every stop is a distinct value. Any
						// write re-broadcasts the whole custom object, and a plain boolean would
						// replay an old stop at the speaker view every time an unrelated setting
						// was saved, locking it mid-presentation.
						presentationStopped: stopped ? new Date().toISOString() : false,
					},
				},
				links: details.links,
			};

			dispatch(saveInspectScreenDetails(accountId, mediaId, mediaNames.medias, body)).then((data: any) => {
				if (!data) {
					// Let the next stop or slide try again rather than going quiet.
					presentationStoppedFlagRef.current = !stopped;
				}
			});
		});
	};

	// the core stop logic, also used for automatic cleanup when the stream stops
	const stopPresentingSlide = () => {
		// The ledger holds every slide on air whoever opened it, so it does the closing; the note
		// is only cleared. Recording the stop is unconditional - with the slides driven from the
		// speaker panel this manager may hold no note at all, and the speaker would never be told.
		closeOpenSequencesNow();
		stoppedPresentingRef.current = true;
		setSelectedSlide(null);
		setPresentationStoppedFlag(true);
		if (liveQueueSlideItem) {
			dispatch(setLiveQueueSlideItem(''));
		}
	};

	const onStopPresentingSlide = () => {
		if (isStreamNotStarted()) { // ask the admin to start the live first
			setShowWarningDialog(true);
			return;
		}
		stopPresentingSlide();
	};

	const presentSlide = (slide: any, index: number) => {
		if (!selectedSlide || selectedSlide.id !== slide.id) {
			const id = generateId();
			const payload = {
				id,
				resourceId: slide.id,
				index: getSlideIndex(slide),
			};
			stoppedPresentingRef.current = false;
			// Clear the stop flag, so an unrelated custom write cannot replay a stale
			// "stopped" at the speaker view mid-presentation.
			setPresentationStoppedFlag(false);
			setSelectedSlide({ ...slide, interactId: id, index });
			setIsSlideLoading(true);
			setLoadingMsg(t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_LOADING_SLIDE'));
			addInteractItemToTracks(payload);
			setNewSlidePayload(payload);
		}
	};

	const handlePresentSlide = (choice: { ok: boolean }) => {
		if (choice.ok) {
			presentSlide(tempSlide.slide, tempSlide.index);
			setHasPresentationStarted(true);
		}
		setShowConfirmationDialog(false);
	};

	const onPresentSlide = (slide: any, index: number) => {
		if (isStreamNotStarted()) { // check if stream has started
			setShowWarningDialog(true);
		} else if (hasPresentationStarted) { // don't ask for confirmation every time
			presentSlide(slide, index);
		} else {
			setTempSlide({ slide, index });
			setShowConfirmationDialog(true);
		}
	};

	const fecthMediaDetails = () => {
		dispatch(getMediaQueryDetails(accountId, mediaId, mediaNames.medias)).then((media: any) => {
			if (media) {
				setMediaDetails(media);
				findAndSetExistingTrack(media.tracks);
			}
		});
	};

	// A slide is closed by whoever opens the next one, and each tool closes the slide *it* opened -
	// this manager from `liveQueueSlideItem`, the speaker panel through the backend. Neither knows
	// what the other did, so every handover between them leaves a slide open for ever and the one
	// that is finally closed is the wrong one.
	//
	// Every open message arrives here whatever opened it, so they are kept as a ledger and anything
	// still open a moment after a new slide goes up is closed. The wait lets the writer that owns
	// the slide close it first; `end` is the new slide's start either way, so waiting longer cannot
	// change what ends up written - only how long an orphan survives.
	// Read through a ref, and the callback itself kept stable: the socket handler below subscribes
	// once, so anything it captures directly would be frozen at that render - including `trackId`,
	// which is only found asynchronously.
	const closeSequenceAtRef = useRef(closeSequenceAt);

	useEffect(() => {
		closeSequenceAtRef.current = closeSequenceAt;
	});

	const closeOrphanedSequencesAfter = useCallback((newestSequenceId: string, newestStart: number) => {
		clearTimeout(orphanCloseTimerRef.current);
		orphanCloseTimerRef.current = setTimeout(() => {
			openSequencesRef.current.forEach((sequence, sequenceId) => {
				if (sequenceId === newestSequenceId || sequence.start >= newestStart) {
					return;
				}
				openSequencesRef.current.delete(sequenceId);
				closeSequenceAtRef.current(sequenceId, sequence.resourceId, sequence.start, newestStart);
			});
		}, ORPHAN_CLOSE_DELAY_MS);
	}, []);

	// Nothing opens a new slide when presenting stops, so the ledger is closed off here instead.
	// It already holds every slide on air, whoever put it there - that is the whole point of it -
	// and an open sequence is dropped when the VOD is built, so the last slide would be lost.
	const closeOpenSequencesAt = useCallback((endSeconds: number) => {
		clearTimeout(orphanCloseTimerRef.current);

		openSequencesRef.current.forEach((sequence, sequenceId) => {
			if (sequence.start >= endSeconds) {
				return;
			}
			openSequencesRef.current.delete(sequenceId);
			closedSequencesRef.current.add(sequenceId);
			closeSequenceAtRef.current(sequenceId, sequence.resourceId, sequence.start, endSeconds);

			// Drop this manager's note of it once closed, so the paths that still write from the
			// note - stopping the presentation, and the prompt before stopping the live - cannot
			// put a second, later end over the one just written.
			if (liveQueueSlideItemRef.current?.id === sequenceId) {
				dispatch(setLiveQueueSlideItem(''));
			}
		});
	}, [dispatch]);

	// Reached from `addToLiveQueue` too, which runs outside this component.
	useEffect(() => {
		closeOpenSequencesRef.current = closeOpenSequencesAt;
	}, [closeOpenSequencesAt]);

	useEffect(() => {
		liveQueueSlideItemRef.current = liveQueueSlideItem;
	}, [liveQueueSlideItem]);

	const closeOpenSequencesNow = useCallback(() => {
		const { liveStreamPositionTime, streamSetup } = store.getState().liveManager;
		const stopTime = streamSetup === streamSetupType.MEETING
			? new Date(liveStreamPositionTime.getTime() + 10000)
			: liveStreamPositionTime;

		closeOpenSequencesAt(getSecondsFromTimelineTime(stopTime));
	}, [store, closeOpenSequencesAt]);

	useEffect(() => () => clearTimeout(orphanCloseTimerRef.current), []);

	useEffect(() => {
		const handleMessage = (evt: MessageEvent) => {
			const message = JSON.parse(evt.data);
			const key = 'Subscribe.On.' + message.key;
			switch (key) {
				case liveWebSocketEvents.slide:
					if (message && message.body && message.body.slideId && message.body.mediaId === mediaId) {
						const { attributes } = message.body;
						if (stoppedPresentingRef.current) {
							return;
						}
						// start can be 0 (slide presented at the very beginning), so check the type instead of truthiness
						const hasStart = typeof attributes.start === "number";
						const hasEnd = typeof attributes.end === "number";
						if (hasStart && hasEnd) {
							openSequencesRef.current.delete(message.body.slideId);
							// Only if it is the slide on screen: closing an older one - which is
							// what the ledger does, a moment after the new slide is up - must not
							// take the highlight off the slide that just went on air.
							setSelectedSlide((current: any) => (
								current?.interactId === message.body.slideId ? null : current
							));
						} else if (hasStart) {
							openSequencesRef.current.set(message.body.slideId, {
								start: attributes.start,
								resourceId: attributes.resource.id,
							});
							closeOrphanedSequencesAfter(message.body.slideId, attributes.start);

							const slide = slides.find((i: any) => i.id === attributes.resource.id);
							const slideIndex = slides.findIndex((i: any) => i.id === attributes.resource.id);
							const id = generateId();
							setSelectedSlide({ ...slide, interactId: id, index: slideIndex });
						}
					}
					break;
				default:
					break;
			}
		};

		registerSocketMessageHandler?.(handleMessage);

		return () => unregisterSocketMessageHandler?.(handleMessage);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mediaId, tempSlide, slides, dispatch, registerSocketMessageHandler, unregisterSocketMessageHandler]);

	useEffect(() => {
		// waits for the new slide to be added and for the interactItems to be updated to add the new slide to the timeline
		if (newSlidePayload && interactItems.length && isSlideLoading) {
			const interactItem = interactItems.find((i: any) => i.id === newSlidePayload.id);
			if (interactItem) {
				if (isAutoAddRef.current) {
					isAutoAddRef.current = false;
					addToLiveQueueAtStart(interactItem);
				} else {
					addToLiveQueue(interactItem);
				}
				setIsSlideLoading(false);
				setLoadingMsg('');
				setNewSlidePayload(null);
			}
		}
	}, [interactItems.length, isSlideLoading, newSlidePayload]);

	useEffect(() => {
		if (liveEventStatus !== liveEventStatusList.STOPPED) {
			return;
		}
		// Runs whether or not this manager thinks it has a slide on air: with the slides driven
		// from the speaker panel it never does, and the last slide would stay open.
		closeOpenSequencesNow();
		if (liveQueueSlideItem) {
			stopPresentingSlide();
		}
	}, [liveEventStatus]);

	useEffect(() => {
		if ((isSlideLoading || isJobOngoing || isFileLoading) && loadingMsg) {
			dispatch(setLoadingOverlayText(loadingMsg));
		} else {
			dispatch(setLoadingOverlayText(''));
		}
	}, [isSlideLoading, isJobOngoing, isFileLoading, loadingMsg]);

	useEffect(() => {
		// stop presentation if liveQueueSlideItem is set to empty. The ledger is consulted too:
		// the note is dropped as soon as the slide it named is closed, and something opened from
		// the speaker panel can still be on air.
		if (!liveQueueSlideItem && !openSequencesRef.current.size) {
			setSelectedSlide(null);
		}

	}, [liveQueueSlideItem]);

	useEffect(() => {
		// Seed once, from the first media that arrives. The actions hook refetches the
		// media on every slide presented, and that read can return the old flag while
		// the clearing write is still in flight - re-seeding from it would leave a
		// stale "true" behind, which then silently swallows the next stop.
		if (!flagSeededRef.current && mediaDetails?.custom?.livemanager) {
			flagSeededRef.current = true;
			presentationStoppedFlagRef.current = !!mediaDetails.custom.livemanager.presentationStopped;
		}
	}, [mediaDetails]);

	useEffect(() => {
		// initialize the slides if not set
		if (slides.length) {
			return;
		}
		const resources = mediaDetails?.asset?.resources;
		if (resources?.length) {
			const slideResources = resources.filter((i: any) => i.id.includes(SLIDE_IDENTIFIER));
			slideResources.length && setShowPresentSection(true);
			slideResources.length && setShowUploadSection(false);

			const sequences = mediaDetails?.tracks?.find((track: any) => track.name === 'pdf-track')?.sequences ?? [];

			// find the slide that is currently being presented (has a start time but no end time).
			// note: start can be 0 when the slide is presented at the very beginning of the stream,
			// so check for null explicitly instead of truthiness (0 is falsy).
			const presentedSequence = [...sequences].reverse().find((el: any) => el.start != null && el.end == null);

			// Seed the ledger, so a page opened mid-session knows what is already on air. Every
			// open sequence goes in, not just the newest: a handover can leave more than one.
			sequences.forEach((sequence: any) => {
				// Media details fetched before a close still show it open, and re-seeding one would
				// have it closed a second time, later, over the end already written.
				if (closedSequencesRef.current.has(sequence.id)) {
					return;
				}
				if (sequence.start != null && sequence.end == null && sequence.resource?.id) {
					openSequencesRef.current.set(sequence.id, {
						start: sequence.start,
						resourceId: sequence.resource.id,
					});
				}
			});
			const presentedSlide = presentedSequence &&
				slideResources.find((src: any) => src.id === presentedSequence.resource?.id);
			if (presentedSlide) {
				setSelectedSlide({
					...presentedSlide,
					interactId: presentedSequence.id,
					index: slideResources.indexOf(presentedSlide),
				});
				setHasPresentationStarted(true);
			}
			setSlides(slideResources);
		}
	}, [mediaDetails, slides.length]);

	useEffect(() => {
		// initialize the selected slide if any after loading all the slides
		if (!liveQueueSlideItem || !slides.length || selectedSlide) {
			return;
		}
		const resourceId = liveQueueSlideItem.resource.id;
		if (resourceId) {
			const slide = slides.find((i: any) => i.id === resourceId);
			if (slide) {
				setSelectedSlide({ ...slide, interactId: liveQueueSlideItem.id, index: slides.indexOf(slide) });
				setHasPresentationStarted(true);
			}
		}
	}, [liveQueueSlideItem, selectedSlide, slides]);

	useEffect(() => {
		// initialize the file name
		if (slides.length) {
			setFileName(`File with ${slides.length} slides`);
			setShowPresentSection(true);
			setShowUploadSection(false);
		}
	}, [slides]);

	useEffect(() => {
		fecthMediaDetails();
	}, []);

	return (
		<div className={styles.container}>
			<div className={styles.uploadSection}>
				<div className={styles.top}>
					<Publish />
					<div className={styles.title}>
						&nbsp;{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_UPLOAD_PRESENTATION')}
					</div>
					<IconButton className={styles.action} onClick={() => setShowUploadSection(prev => !prev)}>
						{showUploadSection ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				</div>
				{showUploadSection &&
					<div className={styles.content}>
						{(isFileLoading || isJobOngoing) ?
							<div className={styles.loader}>
								<ScaleLoader color={'white'} />
							</div> :
							(fileName ?
								<div className={styles.fileContent}>
									{imgUrl &&
										<img src={imgUrl} className={styles.thumbnail} />
									}
									<div className={styles.name}>
										{fileName || imgName}
									</div>
									<div className={styles.actions}>
										<IconButton
											onClick={handleClickRemoveFile}
											disabled={isRemoveFileDisabled}>
											<Close style={{
												color: 'white',
												opacity: isRemoveFileDisabled ? 0.5 : 1
											}} />
										</IconButton>
									</div>
								</div> :
								<div className={styles.chooseBtnContainer}>
									<Button
										color="primary"
										variant="contained"
										onClick={() => fileInputRef.current?.click()}>
										{t('COMMON_CHOOSE_FILE')}
									</Button>
									<input
										type="file"
										ref={fileInputRef}
										style={{ display: "none" }}
										accept=".pdf,.ppt,.pptx"
										onChange={uploadPdfFile}
									/>
								</div>
							)
						}
					</div>
				}
			</div>
			<div className={styles.presentSection}>
				<div className={styles.top}>
					<div className={styles.title}>
						{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_PRESENT_SLIDES')}
					</div>
					<IconButton className={styles.action} onClick={() => setShowPresentSection(prev => !prev)}>
						{showPresentSection ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				</div>
				{showPresentSection &&
					<div className={styles.content}>
						<div className={styles.sectionTop}>
							<div className={styles.label}>
								<ScreenRecordIcon />
								<span>
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_CURRENTLY_PRESENTED_SLIDE')}
								</span>
							</div>
							{selectedSlide && !isSlideLoading &&
								<div className={styles.btnContainer}>
									<Button
										type="button"
										variant="outlined"
										onClick={onStopPresentingSlide}>
										{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_STOP_PRESENTING')}
									</Button>
								</div>
							}
						</div>
						{selectedSlide ?
							<div className={styles.selectedSlideContent}>
								{isSlideLoading ?
									<div className={styles.loader}>
										<ScaleLoader color={'white'} /><br />
									</div> :
									<img src={getSlideImgSrc(selectedSlide)} />
								}
							</div> :
							<div className={styles.chooseSlideContainer}>
								{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_CLICK_TO_START_PRESENTING')}
							</div>
						}
						<div className={styles.actions}>
							<button
								className={`${styles.prev} ${!selectedSlide || isSlideLoading ? styles.disabled : ""}`}
								onClick={presentPrevSlide}
								disabled={!selectedSlide || isSlideLoading}
							>
								<div className={styles.icon}>
									<ChevronLeft style={{ fontSize: 32 }} />
								</div>
								<span>
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_PREVIOUS_SLIDE')}
								</span>
							</button>
							<button
								className={`${styles.next} ${!selectedSlide || isSlideLoading ? styles.disabled : ""}`}
								onClick={presentNextSlide}
								disabled={!selectedSlide || isSlideLoading}
							>
								<span>
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_NEXT_SLIDE')}
								</span>
								<div className={styles.icon}>
									<ChevronRight style={{ fontSize: 32 }} />
								</div>
							</button>
						</div>
						<div className={styles.sectionTop}>
							<div className={styles.label}>
								<span>
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_ALL_SLIDES')} ({ slides.length || '-'})
								</span>
							</div>
						</div>
						{slides.length ?
							<div className={styles.slidesContainer}>
								{slides.map((slide, index) =>
									<Button
										key={slide.id}
										style={{
											padding: 0,
											...(selectedSlide?.id === slide.id ? { border: `3px solid #126CFC` } : {}),
										}}
										onClick={() => onPresentSlide(slide, index)}>
										<img src={getSlideImgSrc(slide)} className={styles.thumbnail} />
									</Button>
								)}
							</div> :
							<div className={styles.slidesEmptyContainer}>
								{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_UPLOAD_TO_SEE_SLIDES')}
							</div>
						}
					</div>
				}
			</div>
			{showConfirmationDialog && (
				<ConfirmationDialog
					title={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_SLIDE_PRESENTER')}
					callback={handlePresentSlide}
					message={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_START_PRESENTING_CONFIRMATION_MSG')}
					okButtonText={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_START_PRESENTING')}
					cancelButtonText={t('COMMON_CANCEL')}
				/>
			)}
			{showWarningDialog && (
				<ConfirmationDialog
					title={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_SLIDE_PRESENTER')}
					callback={() => setShowWarningDialog(false)}
					message={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_START_PRESENTING_WARNING_MSG')}
					okButtonText={t('COMMON_GOT_IT')}
					noCancelButton={true}
				/>
			)}
			{showRemoveFileWarningDialog === "no-delete" && (
				<ConfirmationDialog
					title={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_SLIDE_PRESENTER')}
					callback={() => setShowRemoveFileWarningDialog("")}
					message={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_UPLOADED_FILE_WARNING_MSG')}
					okButtonText={t('COMMON_GOT_IT')}
					noCancelButton={true}
				/>
			)}
			{showRemoveFileWarningDialog === "delete" && (
				<ConfirmationDialog
					title={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_SLIDE_PRESENTER')}
					callback={({ ok }) => {
						setShowRemoveFileWarningDialog("");
						ok && removeFile();
					}}
					message={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER_UPLOADED_FILE_DELETE_MSG')}
					okButtonText={t('COMMON_DELETED')}
				/>
			)}
		</div>
	);
});

export default LiveManagerPresenter;
